/**
 * Cycle 418 — one account at a time, and nothing at all when signed out.
 *
 * ⚠ WHY THIS FILE EXISTS. Sign-out used to be three lines: `clearJwt()`,
 * `clearDeviceToken()`, `clearTokensInNative()`. It discarded credentials and
 * nothing else — it never released the Live-Activity registration, never wiped
 * the widget snapshot, and never purged account-scoped SPA caches. So the
 * server kept a live `live_activity_tokens` row under the OLD account, and
 * cycle-168's `fan_out_reading` kept pushing to it — including PUSH-TO-START,
 * which can create a tile out of nothing on a handset now signed in as someone
 * else. Measured in production before this cycle: one ActivityKit token owned
 * by THREE accounts across seven rows.
 *
 * ⚠ EVERY SIGN-OUT SEAM MUST ROUTE THROUGH `teardownAccount`. The explicit Log
 * out button, the mobile action row, the account pane, the Settings index row,
 * post-account-deletion, AND the cycle-235.1/235.2 genuine-auth-rejection path
 * in `apiClient`. A seam that calls `clearAll()` directly leaves the
 * registrations live — that is the defect, not a shortcut. Pinned by
 * `accountTeardown.test.ts`.
 *
 * ⚠ NATIVE IS REACHED ONLY VIA `window.Capacitor.Plugins` (cycle 138), so the
 * web build, the Pi kiosk and both Android flavors stay inert rather than
 * throwing. Every bridge method used here EXISTS IN THE SHIPPED 0.138.0 SHELL
 * (`AuthBridge.clearTokens`, `LiveActivityBridge.end`,
 * `LiveActivityBridge.getInfo`, `LiveActivityBridge.writeWidgetSnapshot`), so
 * this half lands on the Fly deploy rather than waiting for an archive. That
 * is the whole point of §5.8: if teardown needed a NEW native method, the leak
 * would stay open for however long TestFlight takes.
 */
import {
  clearAll,
  getDeviceToken,
  getJwt,
  markSignedOut,
  wasSignedOut,
} from "./auth";
import { capacitorPlatform } from "./capacitor";
import * as kioskOta from "./kioskOta";
import {
  endLiveActivity,
  clearGlucoseWidgetSnapshot,
  rememberedLiveActivityTokens,
  forgetLiveActivityTokens,
} from "./liveActivity";
import {
  peekRegisteredPushToken,
  rememberedPushTokenHistory,
  forgetRegisteredPushToken,
} from "./nativePush";
import * as devicesApi from "../api/devices";
import * as authApi from "../api/auth";

/** Short, because sign-out must never hang on a network that is not there. */
const RELEASE_TIMEOUT_MS = 2500;

/**
 * ⚠ COLLECTION IS BOUNDED TOO, AND THAT IS NOT BELT-AND-BRACES.
 *
 * The first version of `collectCredentials` awaited
 * `navigator.serviceWorker.ready`. That promise resolves only once an ACTIVE
 * registration exists and **never rejects** — and this app registers its
 * service worker exclusively from `enablePush()`, so on the Android kiosk
 * WebView and every browser that declined Web Push it never settles at all.
 * The `try/catch` around it was useless. `teardownAccount` awaited it before
 * doing anything local, so: the Log Out button did nothing, and a kiosk whose
 * device token had been revoked hung on the splash forever instead of landing
 * on `/pair` — a wall-mounted ALARM SURFACE, bricked by a sign-out.
 *
 * The comment on `teardownAccount` already promised "step 1 must NEVER block
 * steps 2-6". Promising it in prose is not enforcing it, so it is a timeout
 * now: whatever a future collector awaits, the local teardown still runs.
 */
const COLLECT_TIMEOUT_MS = 1500;

/**
 * ⚠ THE ACCOUNT-SCOPED KEYS, DERIVED BY GREPPING THE SPA — NOT IMAGINED.
 *
 * The first draft of this list named `sugalarm.members`, `…activeMemberId`,
 * `…settings` and `…lastReading`. **None of those keys exist** — the SPA
 * holds members, settings and readings in React state and re-fetches them per
 * session — so the purge was a no-op over five invented names while the keys
 * that genuinely carry one account's state across to another sat untouched.
 * Enumerate the real writers before writing a purge list.
 *
 * The two that make this safety-relevant rather than cosmetic:
 *
 *  - **`sugalarm.treatmentQueue`** — the cycle-101 offline dose queue. Left
 *    behind, the NEXT account's first online moment flushes the PREVIOUS
 *    account's pending insulin/carb doses onto ITS member. That is a
 *    cross-account WRITE of a treatment, not a stale read.
 *  - **`sugalarm.pushTokenRegistered` / `sugalarm.nativePush.enabled`** — the
 *    arming state. Inherited, the new account reads "already registered",
 *    skips registering its own token, and ends up an UNPAGEABLE PHONE — the
 *    exact class of defect cycle 365 exists to catch.
 *
 * ⚠ DELIBERATELY NOT PURGED: `sugalarm.signedOut` (the cycle-358 kiosk
 * routing marker — clearing it would land a logged-out kiosk on `/pair`),
 * `sugalarm.ownerKey` (rewritten immediately after), and the device-scoped
 * preferences that are about the SCREEN rather than the account —
 * `alarmTourDone`, `webTheme`, `webAccess`, `settingsCollapse.*`,
 * `alarmReliabilityBannerDismissed`.
 */
const ACCOUNT_SCOPED_KEYS = [
  "sugalarm.treatmentQueue",
  "sugalarm.pushTokenRegistered",
  "sugalarm.nativePush.enabled",
  "sugalarm.nativePush.bundleId",
  "sugalarm.activationAdvisoryOptOut",
  "sugalarm.basalDueDismissedAt",
  "sugalarm.lastPushToken",
  // Cycle 470: the install's remembered push-token history (the superseded
  // tokens a sign-out names alongside the current one). Account-scoped for
  // the same reason as `lastPushToken`: carried across, the NEXT account's
  // sign-out would hand back tokens it never registered (the server's
  // `user_id` predicate makes that a no-op, but the list would be a lie).
  "sugalarm.pushTokenHistory",
  "sugalarm.liveActivityTokens",
  // ⚠ Cycle 434. Counts only — no token, no account id — so leaving it
  // would disclose nothing. It is purged for ACCURACY: carried across a
  // sign-out it reports the previous account's page load under the new
  // account's `client_id`, polluting the one diagnostic the cycle exists
  // to read.
  "sugalarm.refreshDiag",
];

/** Cycle-328 avatar nudge opt-out is keyed `…avatarNudgeOptOut.<memberId>`,
 *  so it needs a prefix sweep rather than an exact name. */
const ACCOUNT_SCOPED_PREFIXES = ["sugalarm.avatarNudgeOptOut."];

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | undefined> {
  return Promise.race([
    p.catch(() => undefined),
    new Promise<undefined>((res) => setTimeout(() => res(undefined), ms)),
  ]);
}

/**
 * The credentials this install holds LOCALLY — the current push token, its
 * remembered history and the Live Activity tokens — as far as the SPA can
 * name them. Best-effort: anything we cannot enumerate is corrected by the
 * cycle-418 transfer-on-register the next time an account signs in here.
 *
 * ⚠ SYNCHRONOUS, ON PURPOSE (cycle 470, review round 1). These reads cannot
 * hang, and they used to sit inside the same 1.5 s timeout as the web-push
 * lookup below, so a `getRegistration()` that never settled discarded the
 * already-collected tokens WITH the result and no release went out at all —
 * no watch flag, no tile release. Only the lookup that CAN hang is bounded.
 */
function collectLocalCredentials(): {
  push_tokens: string[];
  live_activity_tokens: string[];
} {
  const push_tokens: string[] = [];
  const live_activity_tokens: string[] = [];

  // Peek, never take: the token is forgotten only once the server has
  // accepted it back (see `teardownAccount`).
  try {
    const tok = peekRegisteredPushToken();
    if (tok) push_tokens.push(tok);
  } catch {
    /* non-native or unavailable — the transfer path covers it */
  }

  // Cycle 470: the install's remembered history too — the superseded tokens
  // APNs may still accept (cycle 421.1), which a release naming only the
  // current token left live under the old account. A SEPARATE try, so an
  // older test double of this module (one without the export) costs the
  // history and nothing else. Deduplicated against the current token.
  try {
    for (const tok of rememberedPushTokenHistory()) {
      if (tok && !push_tokens.includes(tok)) push_tokens.push(tok);
    }
  } catch {
    /* no history — the current token above still goes */
  }

  // From the listener that registered them, NOT from `getInfo` — the bridge
  // does not return tokens. See `liveActivity.rememberedLiveActivityTokens`.
  try {
    live_activity_tokens.push(...rememberedLiveActivityTokens());
  } catch {
    /* storage unavailable */
  }

  return { push_tokens, live_activity_tokens };
}

/** The browser's Web Push endpoint, if any — the ONE lookup that can hang,
 *  so it is the one `teardownAccount` bounds with `COLLECT_TIMEOUT_MS`. */
async function collectWebPushEndpoints(): Promise<string[]> {
  const web_push_endpoints: string[] = [];
  try {
    // ⚠ `getRegistration()`, NEVER `.ready`. `.ready` hangs forever with no
    // active worker; `getRegistration()` resolves `undefined`. This is the
    // idiom `push.ts` already uses at both of its call sites.
    if (typeof navigator !== "undefined" && navigator.serviceWorker) {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager?.getSubscription?.();
      if (sub?.endpoint) web_push_endpoints.push(sub.endpoint);
    }
  } catch {
    /* no service worker */
  }
  return web_push_endpoints;
}

/**
 * Cycle 470 — is THIS install the one that can hold a paired watch?
 *
 * ⚠ The account-wide watch release (`release_watch_tokens`) is sent only from
 * the iOS native shell: the `WatchConnectivityRelay` that pairs a watch lives
 * in the iOS App target and nowhere else. The first build sent the flag from
 * every surface, so signing out of the web dashboard on a laptop — while the
 * iPhone paired to the watch stayed signed in — would have deleted the
 * account's watch rows and taken the wrist rail until the watch app next
 * relaunched (review round 1). A web, PWA, Android or kiosk sign-out still
 * names its OWN tokens; it just never speaks for the wrist.
 */
function thisInstallPairsAWatch(): boolean {
  try {
    return capacitorPlatform() === "ios";
  } catch {
    return false;
  }
}

/**
 * Cycle 471 — did another credential land on this screen while the teardown
 * was waiting? A DIFFERENT device token (a pairing that completed) or a JWT
 * that was not there when we started (a sign-in at `/login`). Our own
 * credentials being GONE is not a takeover (nothing else cleared them yet,
 * and a null reads as "still ours, not yet wiped"). Never throws.
 */
function screenChangedHands(
  deviceTokenAtStart: string | null,
  jwtAtStart: string | null,
): boolean {
  try {
    const dt = getDeviceToken();
    if (dt !== null && dt !== deviceTokenAtStart) return true;
    const jwt = getJwt()?.access ?? null;
    return jwt !== null && jwt !== jwtAtStart;
  } catch {
    return false;
  }
}

/**
 * Tear down everything this install holds for the current account.
 *
 * ⚠ ORDER IS LOAD-BEARING, and step 1 must NEVER block steps 2-6. An offline
 * sign-out completes locally and completely; the missed release is corrected
 * by transfer-on-register the next time anyone signs in on this handset. A
 * sign-out that hangs is worse than a release that arrives late.
 */
export async function teardownAccount(
  opts: { explicit: boolean } = { explicit: true },
): Promise<void> {
  // ⚠ CYCLE 419 — CAPTURE THE ACCESS TOKEN BEFORE ANYTHING RUNS.
  //
  // The comment below used to claim "the requests capture their auth header
  // when they are issued, so discarding the JWT at step 6 cannot strand
  // them." That is exactly backwards for the LAST request in the chain. The
  // network work is fired and not awaited, so steps 2-6 race it — and step 6
  // is `clearAll()`. The logout POST is issued only after a release
  // round-trip and a `disablePush()`, so by the time `request()` reads
  // `getBearer()` the credentials are usually already gone and the call goes
  // out unauthenticated: it 401s, revokes nothing, and drives the 401 dance
  // into a second teardown. Since cycle 419 that call is the ONLY thing that
  // ends the session server-side, so it is handed the token explicitly.
  //
  // The JWT specifically, never `getBearer()`: on a paired kiosk the device
  // token wins there, and a `dt_` credential carries no session to revoke.
  const jwtAtStart = (() => {
    try {
      return getJwt()?.access ?? null;
    } catch {
      return null;
    }
  })();
  // Cycle 471 — THE DEVICE TOKEN TOO, FOR THE SAME REASON. A paired kiosk
  // runs on its `dt_` credential and holds no JWT, so until this cycle its
  // sign-out reached the server with NOTHING: the logout was skipped (no
  // session), `/api/devices/release` is JWT-only, and the row kept a live
  // token forever. The credential is captured before `clearAll()` wipes it
  // and handed to the self-revoke explicitly. (A `try`: an older test
  // double of `./auth` may not export it; a kiosk then simply clears
  // locally, as it always has.)
  const deviceTokenAtStart = (() => {
    try {
      return getDeviceToken();
    } catch {
      return null;
    }
  })();

  // ── 1. While the JWT is still valid, hand the credentials back. ────────
  // Fired, deliberately NOT awaited before the local teardown runs. Since
  // cycle 470 the release carries `jwtAtStart` explicitly too (it is issued
  // before `clearAll()` and so went out authenticated already; the explicit
  // bearer plus `noRetry` is what keeps a stray 401 out of the refresh dance),
  // and the logout has carried it since cycle 419. `disablePush()` is the one
  // call in the chain that still reads the live bearer: it runs after the
  // release and before the logout, and unsubscribes the browser regardless.
  const local = collectLocalCredentials();
  const web_push_endpoints =
    (await withTimeout(collectWebPushEndpoints(), COLLECT_TIMEOUT_MS)) ?? [];

  // ── 1a. Cycle 471: a KIOSK signs out of ITSELF, server-side, FIRST. ───
  // AWAITED, bounded by `RELEASE_TIMEOUT_MS`, and BEFORE `clearAll()`: the
  // `dt_` credential is the only thing that can authenticate this call, and
  // step 6 destroys it. The route (`POST /api/devices/self/revoke_token`,
  // device-token only) clears exactly the calling row's token and releases
  // exactly that device's push rows — the household's phones, the sibling
  // kiosk and every other account survive by the server's predicates, so a
  // wall sign-out can never silence anyone else (cycle 419). ONLY on an
  // EXPLICIT sign-out (cycles 235.2 / 418: an auth rejection sends nothing —
  // a revoked kiosk landing on `/pair` has nothing left to revoke anyway).
  // On a timeout or a refusal the local wipe still runs: a kept credential
  // is a credential a stolen tablet can present, so nothing is parked for a
  // retry; the backstops are the APK's `clearToken`, the admin cleanup and
  // a same-account re-pair, which `approve` collapses onto the same row.
  // A 401 means "already revoked" (the credential is gone) and counts as
  // done. Inside its own try/catch so steps 2-6 can never be blocked by it.
  if (opts.explicit && deviceTokenAtStart) {
    let accepted = false;
    try {
      const res = await withTimeout(
        devicesApi.selfRevokeToken(
          { push_tokens: local.push_tokens },
          deviceTokenAtStart,
        ),
        RELEASE_TIMEOUT_MS,
      );
      accepted = !!res && (res.ok || res.error.status === 401);
    } catch {
      /* clear locally regardless */
    }
    // ⚠ THE SCREEN MAY HAVE CHANGED HANDS DURING THE AWAIT (astra review,
    // round 1). The bounded wait above is the one place this teardown yields
    // before the local wipe; a pairing or a sign-in that landed on this
    // screen inside it has written NEW credentials, and the continuation
    // below (`purgeAccountScopedCaches`, `clearAll()`, `clearToken`) would
    // wipe THEM — the next account's pairing, not ours. The server side of
    // OUR sign-out has already run (or timed out); the new account's own
    // sign-in landing purged our caches (`purgeOnOwnerMismatch`). So stop.
    // (`useAuth.signOut` still flips its React state to signed-out in the
    // tab that started this; a pairing's hard reload makes that moot, and
    // in the same tab a sign-in cannot land during the await because the
    // sign-in and pairing screens are not mounted while signed in. A future
    // same-tab landing path must re-hydrate after this return.)
    if (screenChangedHands(deviceTokenAtStart, jwtAtStart)) return;
    // Only now, with the screen still ours, is the push-token bookkeeping
    // forgotten (astra review, pass 2). `forgetRegisteredPushToken()`
    // clears BOTH the current-token key and the cycle-470 history, and a
    // replacement account that landed during the await has already written
    // ITS registration under those keys — wiping it would strip that
    // account's named cleanup at its own next sign-out. A 401 counts as
    // accepted (the credential is gone); a refusal or a timeout skips THIS
    // explicit forget — the one that marks the server as having taken the
    // credential back — and leaves the two keys to `purgeAccountScopedCaches`
    // in step 5 (the cycle-470 footnote: a refused revoke is not retried by
    // the next sign-out).
    if (accepted) forgetRegisteredPushToken();
  }

  const creds: devicesApi.DeviceReleaseIn = {
    push_tokens: local.push_tokens,
    live_activity_tokens: local.live_activity_tokens,
    web_push_endpoints,
    release_watch_tokens: false,
  };
  // ⚠ AN EXPIRED SESSION IS NOT A SIGN-OUT — LOWS KILL.
  //
  // Only an EXPLICIT sign-out hands back the alarm rails. On a genuine auth
  // rejection (a refresh token that aged out at 3 a.m.) the person did not
  // ask to leave, and deregistering `device_push_tokens` / `push_subscriptions`
  // would silence the handset for every low until someone next signs in —
  // `useActivationGuard` only re-arms on an AUTHED launch. Losing the low rail
  // on a non-user-initiated event is exactly what the standing rule forbids.
  //
  // Nothing is leaked by keeping them: the rows still belong to the SAME
  // account, so there is no cross-tenant exposure, and the moment a different
  // account signs in here the cycle-418 transfer-on-register re-points every
  // one of them. The Live Activity is still released either way — a signed-out
  // device must not carry a glucose tile.
  if (!opts.explicit) {
    creds.push_tokens = [];
    creds.web_push_endpoints = [];
  }
  // ⚠ Cycle 470 — THE WATCH FOLLOWS THE SAME LINE. The watch registers its
  // OWN APNs token from the watch app, so this install can never name it
  // above; an EXPLICIT sign-out of the one install that can pair a watch
  // (the iOS shell, `thisInstallPairsAWatch`) instead asks the server to
  // release every watch token under the account (`release_watch_tokens:
  // true`, behind the server's kill switch). An auth rejection, and any
  // other surface, sends `false`: the wrist keeps its registration for the
  // SAME account, exactly like the phone's.
  creds.release_watch_tokens = opts.explicit && thisInstallPairsAWatch();
  // ⚠ Cycle 470 (review round 1) — A LATE RESULT MUST NOT ACT ON THE NEXT
  // ACCOUNT. The network chain below is not awaited, and a different account
  // can sign in on this install before it settles. The server is safe (every
  // predicate carries the signed-out account's id); the LOCAL callbacks were
  // not: an accepted release forgot the NEW account's freshly remembered
  // token, and `disablePush()` unsubscribed its freshly enabled web-push
  // rail. Either runs only while nobody else has signed in since.
  const takenOver = (): boolean => {
    try {
      const now = getJwt()?.access ?? null;
      return now !== null && now !== jwtAtStart;
    } catch {
      return false;
    }
  };
  const network = (async () => {
    // Gated on the JWT: `/api/devices/release` is JWT-only (a kiosk's `dt_`
    // token is refused with 403 — cycle 471 gives the kiosk its own door),
    // so a device session issuing it today only buys a refusal.
    if (jwtAtStart) {
      const res = await withTimeout(
        devicesApi.releaseCredentials(creds, jwtAtStart),
        RELEASE_TIMEOUT_MS,
      );
      // Forget the credentials on an ACCEPTED release — the explicit success
      // path. (Honest footnote, cycle 470: step 5's `purgeAccountScopedCaches()`
      // removes the same keys whatever happens here, so a release that timed
      // out or 401'd is NOT retried by the next sign-out on this device. That
      // is a recorded residual, kept so the next account's sign-out never
      // names the previous account's tokens; the server's `user_id` predicate
      // and the cycle-418 re-pointing upsert are what make the loss harmless.)
      if (res?.ok && !takenOver()) {
        forgetRegisteredPushToken();
        forgetLiveActivityTokens();
      }
    }
    // ⚠ AFTER the release, never before. `disablePush()` unsubscribes the
    // browser, so calling it first (as `useAuth.signOut` used to) destroyed
    // the endpoint before anything could collect it — `web_push_endpoints`
    // was empty on every explicit sign-out.
    if (opts.explicit) {
      try {
        const { disablePush } = await import("./push");
        // Re-checked AFTER the import (security review, round 2): the chunk
        // load can take a network round trip, and a sign-in that landed
        // inside it would otherwise lose its fresh subscription here.
        if (!takenOver()) {
          // Cycle 471: the guard is handed IN and re-read by `disablePush`
          // right before `unsubscribe()` — the two local awaits inside it
          // were the one window this check could not cover (the 470
          // review's recorded gap, closed by operator decision at that
          // deploy).
          await withTimeout(
            disablePush({ proceed: () => !takenOver() }),
            RELEASE_TIMEOUT_MS,
          );
        }
      } catch {
        /* best effort */
      }
    }
    // ⚠ THIS IS THE REVOCATION. Since cycle 419 `POST /api/auth/logout`
    // sets `revoked_at` on EXACTLY ONE session row — the one named by the
    // `sid` claim inside `jwtAtStart`. It reaches no other device, no other
    // account, and above all not the household kiosk, which authenticates
    // with a `dt_` device token and holds no JWT session at all.
    //
    // Skipped entirely when this install has no JWT (a kiosk running on its
    // device token): there is no session to end, and an unauthenticated POST
    // would only 401.
    if (jwtAtStart) {
      await withTimeout(authApi.logout(jwtAtStart), RELEASE_TIMEOUT_MS);
    }
  })();
  void network;

  // ── 2. End any running Live Activity and clear its App-Group state. ────
  await endLiveActivity().catch(() => undefined);

  // ── 3. Wipe the widget snapshot so the face falls to the placeholder. ──
  await clearGlucoseWidgetSnapshot().catch(() => undefined);

  // ── 4. Relay a signed-out context to the watch. ────────────────────────
  // Driven by `clearTokensInNative()` inside `clearAll()` below: emptying the
  // App-Group pair makes `WatchConnectivityRelay.currentContext()` return
  // `{signedOut: true}`, which the watch treats as an explicit purge
  // (`WatchSession.ingest`). On 0.140.0 `clearTokens` republishes immediately;
  // on the shipped 0.138.0 it republishes on the next foreground / WC
  // activation / reachability change, so the watch purges then.

  // ── 5. Purge account-scoped SPA caches. ───────────────────────────────
  purgeAccountScopedCaches();

  // ── 6. Discard the credentials LAST. ──────────────────────────────────
  clearAll();
  // Cycle 471 — then tell the kiosk APK to drop the token IT holds, so the
  // native heartbeat stops presenting the credential the server has just
  // revoked (`OtaUpdater.reportContext` ignores an EMPTY token on purpose, so
  // the periodic report alone never cleared it). After `clearAll()`, never
  // before: the self-revoke above needed the token. A no-op off the kiosk
  // APK; an old APK ignores the field and stops at its next restart or OTA.
  if (opts.explicit) {
    try {
      void kioskOta.clearToken();
    } catch {
      /* the native bridge is best effort */
    }
  }
  // ⚠ CYCLE 358 SEMANTICS, PRESERVED EXACTLY. The marker is set AFTER
  // `clearAll()` so the teardown cannot wipe it — and ONLY for an EXPLICIT
  // sign-out. `clearAll()` also runs on a genuine auth REJECTION, and a kiosk
  // whose device token was revoked must land on `/pair`, not `/login`.
  // Collapsing the two would strand a wall-mounted alarm surface on a login
  // screen it cannot satisfy.
  if (opts.explicit) markSignedOut();
}

/** The cycle-101 offline dose queue — singled out because discarding it is a
 *  CLINICAL loss, not a cache miss. See `purgeAccountScopedCaches`. */
const DOSE_QUEUE_KEY = "sugalarm.treatmentQueue";

/**
 * @param keepDoseQueue - true ONLY on the one-time upgrade path (a device that
 *   has never stored an owner key). See the call in `purgeOnOwnerMismatch`.
 */
export function purgeAccountScopedCaches(keepDoseQueue = false): void {
  try {
    for (const k of ACCOUNT_SCOPED_KEYS) {
      if (keepDoseQueue && k === DOSE_QUEUE_KEY) continue;
      localStorage.removeItem(k);
    }
    // Prefix-keyed families (cycle-328 avatar nudge is per member id).
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && ACCOUNT_SCOPED_PREFIXES.some((p) => k.startsWith(p))) {
        doomed.push(k);
      }
    }
    for (const k of doomed) localStorage.removeItem(k);
  } catch {
    /* storage unavailable */
  }
}

const OWNER_KEY_STORAGE = "sugalarm.ownerKey";

/**
 * ⚠ THE MOST IMPORTANT LINE OF CLIENT CODE IN THIS CYCLE — it repairs a
 * handset whose sign-out never ran at all.
 *
 * Called immediately after EVERY successful sign-in, including `adoptSession`
 * and the Google / Apple / passkey landings. If the incoming account differs
 * from whatever this device last held — or if anything is stored with no owner
 * key at all, which is every pre-upgrade device — everything from steps 2-5 is
 * wiped BEFORE any snapshot, auth mirror or Live Activity is written.
 *
 * Absent must mean UNKNOWN, never match. A device that cannot prove whose data
 * it is holding is a device that must not render it.
 */
export function purgeOnOwnerMismatch(
  incomingOwnerKey: string | null | undefined,
): Promise<void> {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(OWNER_KEY_STORAGE);
  } catch {
    stored = null;
  }

  const matches = !!incomingOwnerKey && !!stored && incomingOwnerKey === stored;

  // ⚠ THE SYNCHRONOUS HALF RUNS BEFORE THIS FUNCTION EVER YIELDS, and that
  // is load-bearing. Callers invoke it from synchronous sign-in landings as
  // `void purgeOnOwnerMismatch(...)` immediately BEFORE `setJwt`, so the
  // caches are gone and the new owner key is recorded before any effect can
  // observe a signed-in state and write a snapshot. Anything awaited below
  // is best-effort cleanup of a surface the app is about to overwrite
  // anyway; anything that MUST happen first belongs above the first await.
  // ⚠ UNKNOWN AND MISMATCHED ARE BOTH PURGES, BUT NOT THE SAME PURGE.
  //
  // `stored === null` is every device on its FIRST LAUNCH AFTER THIS DEPLOY —
  // overwhelmingly a correctly signed-in device that simply predates the owner
  // key. Treating that as an account switch and dropping
  // `sugalarm.treatmentQueue` would silently discard insulin and carbs a
  // person logged while offline: they never reach the server, and IOB then
  // UNDERSTATES insulin on board, which is a dosing error, not a stale cache.
  //
  // A PROVEN mismatch (a key is stored and it differs) still drops it, and
  // must: `POST /api/treatments` resolves the member from the caller's ACTIVE
  // member, not from the payload, so a foreign queued dose would be written
  // onto the new account's member as phantom IOB.
  //
  // Everything else is purged in both cases — only the dose queue gets the
  // benefit of the doubt, and only once per device.
  const firstLaunchAfterUpgrade = !stored;
  if (!matches) purgeAccountScopedCaches(firstLaunchAfterUpgrade);

  try {
    if (incomingOwnerKey) {
      localStorage.setItem(OWNER_KEY_STORAGE, incomingOwnerKey);
    } else {
      localStorage.removeItem(OWNER_KEY_STORAGE);
    }
  } catch {
    /* storage unavailable */
  }

  if (matches) return Promise.resolve();

  return (async () => {
    await endLiveActivity().catch(() => undefined);
    await clearGlucoseWidgetSnapshot().catch(() => undefined);
  })();
}

/** Re-export so callers never reach past this module for the marker. */
export { wasSignedOut };
